import React                       from 'react';
import { observer }                from 'mobx-react-lite';
import View                        from '@widesk-ui/components/View';
import Button                      from '@widesk-ui/components/Button';
import Segmented                   from '@widesk-ui/Segmented';
import { CheckOutlined }           from '@ant-design/icons';
import { DownOutlined }            from '@ant-design/icons';
import { WarningOutlined }         from '@ant-design/icons';
import { AimOutlined }             from '@ant-design/icons';
import { SearchOutlined }          from '@ant-design/icons';
import { FRAMING_MAX_PITCH_DEG }   from '@tokamap/core';
import { FRAMING_MAX_ZOOM_FACTORS } from '@tokamap/core';
import { FramingMargin }           from '@tokamap/core';
import { MapFraming }              from '@tokamap/core';
import { rectAroundPoints }        from '@tokamap/core';
import { FRAMING_MARGIN_RATIOS }   from '@tokamap/core';
import { lngLatToMercator }        from '@tokamap/core';
import { rectCorners }             from '@tokamap/core';
import SliderInputNumber           from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import InputNumber                 from '@widesk-ui/components/form/InputNumber';
import Space                       from '@widesk-ui/components/Space';
import currentEditorStore          from '@/TokamapEditor/stores/currentEditorStore';
import framingStore                from '@/TokamapEditor/stores/framingStore';
import { FramingQuestion }         from '@/TokamapEditor/stores/framingStore';
import useFraming                  from '@/TokamapEditor/ux/Framing/useFraming';
import shapesOutsideLimit          from '@/TokamapEditor/ux/Framing/shapesOutsideLimit';
import { startFrameIsEmpty }       from '@/TokamapEditor/ux/Framing/shapesOutsideLimit';
import PlaceSearchField            from '@forms/map/PlaceSearchField';
import { LngLat }                  from '@tokamap/core';
import { AddressSearchResult }     from '@tokamap/core';
import { FramingRect }             from '@tokamap/core';
import useDesignTokens             from '@/hooks/useDesignTokens';

const SETTINGS_PANEL_WIDTH = 400;
const MIN_PLACE_FRAME_METERS = 150;
const FRAMING_VIEW_PADDING = { top: 32, right: 32, bottom: 32, left: 432 };

const MARGINS: { value: FramingMargin; label: string; share: string }[] = [
	{ value: 'tight', label: 'Serrée', share: 'les trois quarts' },
	{ value: 'normal', label: 'Normale', share: 'la moitié' },
	{ value: 'wide', label: 'Large', share: 'un tiers' },
];

const PITCH_STEPS = [
	{ value: 0, label: 'À plat' },
	{ value: 30, label: 'Légère' },
	{ value: 55, label: 'Forte' },
];

const DETAIL_LEVELS: [number, string][] = [
	[13, 'ville'],
	[15, 'quartier'],
	[17, 'rue'],
	[19, 'bâtiment'],
	[Infinity, 'stand'],
];

const FREE_ZOOM_MAX_FACTOR = 32;

const formatFactor = (factor: number) => `×${String(factor).replace('.', ',')}`;

const FramingSection = observer(() => {
	const tokens = useDesignTokens();
	const { framing, framingKey, shapesRevision, geometry, isPlaced, allowPitch, update } = useFraming();
	const editor = currentEditorStore.get;
	const question = framingStore.question;

	React.useEffect(() => {
		if (question === 'start') editor.mapAdapter.showFramingRect('start', FRAMING_VIEW_PADDING);
		if (question === 'limit') editor.mapAdapter.showFramingRect('limit', FRAMING_VIEW_PADDING);
	}, [editor, question]);

	const limitMode = !isPlaced && framing.limit.mode === 'none' ? 'content' : framing.limit.mode;
	const margin = framing.limit.mode === 'content' ? framing.limit.margin : 'normal';
	const [searchingPlace, setSearchingPlace] = React.useState(false);
	const outside = React.useMemo(() => shapesOutsideLimit(framing, geometry), [framingKey, shapesRevision]);
	const startIsEmpty = React.useMemo(() => startFrameIsEmpty(framing, geometry), [framingKey, shapesRevision]);
	const factorIsStep = (FRAMING_MAX_ZOOM_FACTORS as readonly number[]).includes(framing.maxZoomFactor);

	React.useEffect(() => {
		if (question === 'zoom') framingStore.setFreeZoom(!factorIsStep);
		else framingStore.setFreeZoom(false);
	}, [question]);

	const detailLevel = DETAIL_LEVELS.find(([zoom]) => (geometry.maxZoom ?? 0) < zoom)?.[1];

	const frameOnVisibleArea = (): Partial<MapFraming> => {
		const container = editor.mapAdapter.getContainerElement();
		const view = editor.getMapView();
		const width = container?.clientWidth || 1280;
		const height = container?.clientHeight || 800;
		const left = Math.min(SETTINGS_PANEL_WIDTH, width - 1);
		const corners = [[left, 0], [width, 0], [width, height], [left, height]]
			.map(([x, y]) => lngLatToMercator(editor.mapAdapter.screenToLngLat(x!, y!)));

		const frame = rectAroundPoints(corners, view.rotationDeg)!;

		return {
			start: { mode: 'frame', frame },
			rotationDeg: view.rotationDeg,
			...limitAroundFrame(frame),
		};
	};

	const tilt = (pitchDeg: number, live = false) => {
		update({ pitchDeg }, live);

		if (live) editor.mapAdapter.setPitch(pitchDeg);
		else editor.mapAdapter.animateToView({ pitchDeg });
	};

	const setFreeMaxZoomFactor = (value: number, live = false) => {
		const maxZoomFactor = Math.min(FREE_ZOOM_MAX_FACTOR, Math.max(1, Math.round(value * 10) / 10));

		update({ maxZoomFactor }, live);

		const maxZoom = editor.mapAdapter.getFramingGeometry()?.maxZoom;
		if (typeof maxZoom !== 'number') return;

		if (live) editor.mapAdapter.setZoom(maxZoom);
		else editor.mapAdapter.animateToView({ zoom: maxZoom });
	};

	const relocateStart = (place: AddressSearchResult) => {
		if (framing.start.mode !== 'frame') return;

		const current = framing.start.frame;
		const around = place.bounds
			? rectAroundPoints([
				[place.bounds[0][0], place.bounds[0][1]],
				[place.bounds[1][0], place.bounds[0][1]],
				[place.bounds[1][0], place.bounds[1][1]],
				[place.bounds[0][0], place.bounds[1][1]],
			].map(point => lngLatToMercator(point as LngLat)), framing.rotationDeg)
			: null;
		const frame = around
			? { center: around.center, width: Math.max(around.width, MIN_PLACE_FRAME_METERS), height: Math.max(around.height, MIN_PLACE_FRAME_METERS) }
			: { ...current, center: lngLatToMercator(place.center) };

		update({ start: { mode: 'frame', frame }, ...limitAroundFrame(frame) });
		editor.mapAdapter.showFramingRect('start', FRAMING_VIEW_PADDING);
	};

	const limitAroundFrame = (frame: FramingRect): Partial<MapFraming> => {
		if (framing.limit.mode !== 'frame') return {};

		const margin = Math.max(frame.width, frame.height) * FRAMING_MARGIN_RATIOS.tight;

		return {
			limit: {
				mode: 'frame',
				frame: { center: frame.center, width: frame.width + 2 * margin, height: frame.height + 2 * margin },
			},
		};
	};

	const setMaxZoomFactor = (maxZoomFactor: number) => {
		framingStore.setFreeZoom(false);
		update({ maxZoomFactor });

		const maxZoom = editor.mapAdapter.getFramingGeometry()?.maxZoom;
		if (typeof maxZoom === 'number') editor.mapAdapter.animateToView({ zoom: maxZoom });
	};

	const updateLimit = (limit: MapFraming['limit']) => {
		update({ limit });
		editor.mapAdapter.showFramingRect('limit', FRAMING_VIEW_PADDING);
	};

	const orient = (rotationDeg: number, live = false) => {
		update({ rotationDeg }, live);

		if (live) editor.mapAdapter.setRotation(rotationDeg);
		else editor.mapAdapter.animateToView({ rotationDeg });
	};

	const startSummary = [
		framing.start.mode === 'content' ? 'Tout le plan' : 'Une zone choisie',
		framing.rotationDeg ? `${Math.round(framing.rotationDeg)}°` : 'nord en haut',
		allowPitch && framing.pitchDeg ? `incliné ${Math.round(framing.pitchDeg)}°` : null,
	].filter(Boolean).join(' · ');

	const limitSummary = {
		none: 'Partout',
		content: `Autour du plan · marge ${MARGINS.find(m => m.value === margin)!.label.toLowerCase()}`,
		frame: 'Une zone tracée',
	}[limitMode];

	return (
		<View gap="sm">
			<QuestionCard
				question="start"
				number={1}
				title="Que voit-on en arrivant ?"
				summary={startSummary}
				color={tokens.color.primary}
				warning={startIsEmpty ? 'Aucune forme dans la vue de départ' : undefined}
			>
				<Choice
					label="Tout le plan"
					hint="En entier sur chaque écran, et le cadre suit vos dessins."
					selected={framing.start.mode === 'content'}
					onSelect={() => update({ start: { mode: 'content' } })}
				/>

				<Choice
					label="Une zone choisie"
					hint="Ajustez le cadre sur la carte avec ses poignées."
					selected={framing.start.mode === 'frame'}
					onSelect={() => update(geometry.startRect
						? { start: { mode: 'frame', frame: geometry.startRect } }
						: frameOnVisibleArea())}
				/>

				{framing.start.mode === 'frame' && (
					<>
						{searchingPlace ? (
							<div onKeyDown={event => event.key === 'Escape' && setSearchingPlace(false)}>
								<PlaceSearchField
									value={null}
									hint={null}
									autoFocus
									onBlur={() => setSearchingPlace(false)}
									onChange={place => {
										if (place) relocateStart(place);
										setSearchingPlace(false);
									}}
								/>
							</div>
						) : (
							<View row gap="xs">
								{isPlaced && (
									<Button size="small" icon={<SearchOutlined />} onClick={() => setSearchingPlace(true)}>
										Aller à un lieu
									</Button>
								)}

								<Button size="small" icon={<AimOutlined />} onClick={() => update(frameOnVisibleArea())} style={{ flex: 1 }}>
									Cadrer sur l'affichage
								</Button>
							</View>
						)}

						<Hint>Déplacez et zoomez la carte, puis « Cadrer sur l'affichage » : le cadre reprend exactement la partie visible.</Hint>
					</>
				)}

				{startIsEmpty && (
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: 6,
							padding: 8,
							borderRadius: 8,
							fontSize: 12.5,
							lineHeight: 1.4,
							background: `color-mix(in srgb, ${tokens.color.warning} 12%, transparent)`,
						}}
					>
						<WarningOutlined style={{ color: tokens.color.warning, marginTop: 3, flexShrink: 0 }} />
						<span>Aucune forme dans la vue de départ : le visiteur arrivera sur une partie vide de la carte.</span>
					</div>
				)}

				<View gap="xs" pt="xs">
					<View row centerV gap="sm">
						<View flex bold size={12.5}>Orientation</View>

						<Space.Compact size="small">
							<InputNumber
								size="small"
								min={-180}
								max={180}
								step={1}
								precision={0}
								changeOnWheel
								prefix={<Compass rotationDeg={framing.rotationDeg} />}
								formatter={value => `${value ?? 0}°`}
								parser={value => Number((value ?? '').replace('°', '')) || 0}
								value={Math.round(framing.rotationDeg)}
								onChange={value => orient(normalizeDegrees(Number(value) || 0))}
								style={{ width: 96 }}
							/>

							<Button size="small" disabled={!Math.round(framing.rotationDeg)} onClick={() => orient(0)}>
								Nord
							</Button>
						</Space.Compact>
					</View>

					<Hint>Faites aussi pivoter le cadre avec sa poignée ronde, sur la carte.</Hint>
				</View>

				{isPlaced && allowPitch && (
					<SubField label="Inclinaison 3D">
						<Segmented
							block
							size="small"
							value={PITCH_STEPS.find(step => step.value === Math.round(framing.pitchDeg))?.value ?? -1}
							onChange={value => tilt(Number(value))}
							options={PITCH_STEPS}
						/>

						<SliderInputNumber
							min={0}
							max={FRAMING_MAX_PITCH_DEG}
							step={1}
							suffix="°"
							value={Math.round(framing.pitchDeg)}
							onChange={value => tilt(Number(value) || 0, true)}
							onAfterChange={value => tilt(Number(value) || 0)}
						/>

						<Hint>
							{framing.pitchDeg > 50
								? 'Très inclinée : le plan paraîtra plus petit sur la vue de départ, et l\'horizon apparaît.'
								: 'Vérifiez le rendu sur mobile ou tablette avec « Aperçu ».'}
						</Hint>
					</SubField>
				)}
			</QuestionCard>

			<QuestionCard
				question="limit"
				number={2}
				title="Jusqu'où peut-on aller ?"
				summary={limitSummary}
				color={tokens.text.secondary}
				warning={outside.length ? `${outside.length} forme${outside.length > 1 ? 's' : ''} hors de la limite` : undefined}
			>
				{isPlaced && (
					<Choice
						label="Partout"
						hint="Aucune limite : on peut parcourir le monde entier."
						selected={limitMode === 'none'}
						onSelect={() => updateLimit({ mode: 'none' })}
					/>
				)}

				<Choice
					label="Autour du plan"
					hint="Une marge proportionnelle à la taille du plan, qui le suit."
					selected={limitMode === 'content'}
					onSelect={() => updateLimit({ mode: 'content', margin })}
				>
					<Segmented
						block
						size="small"
						value={margin}
						onChange={value => updateLimit({ mode: 'content', margin: value as FramingMargin })}
						options={MARGINS.map(({ value, label }) => ({ value, label }))}
					/>

					<Hint>
						Au plus loin, le plan occupe encore {MARGINS.find(m => m.value === margin)!.share} de l'écran.
					</Hint>
				</Choice>

				<Choice
					label="Une zone tracée"
					hint="Ajustez la limite sur la carte avec ses poignées."
					selected={limitMode === 'frame'}
					onSelect={() => {
						const frame = geometry.limitRect ?? geometry.startRect;

						if (frame) updateLimit({ mode: 'frame', frame });
					}}
				/>

				{!!outside.length && (
					<View gap="xs" p="sm" br={8} bg={`color-mix(in srgb, ${tokens.color.warning} 12%, transparent)`}>
						<div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, lineHeight: 1.4 }}>
							<WarningOutlined style={{ color: tokens.color.warning, marginTop: 3, flexShrink: 0 }} />
							<span>
								{outside.length > 1
									? `${outside.length} formes dépassent la limite : les visiteurs ne pourront pas les voir en entier.`
									: 'Une forme dépasse la limite : les visiteurs ne pourront pas la voir en entier.'}
							</span>
						</div>

						<div style={{ display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap' }}>
							<Button size="small" onClick={() => updateLimit(extendedLimit(framing, outside.flatMap(shape => shape.points)))}>
								Agrandir la zone
							</Button>

							<Button size="small" type="link" style={{ padding: 0 }} onClick={() => updateLimit({ mode: 'content', margin })}>
								Passer à « Autour du plan »
							</Button>
						</div>
					</View>
				)}

				<Hint>
					{limitMode === 'none'
						? 'Le dézoom n\'est pas limité.'
						: 'On ne voit jamais au-delà de la zone : sur un écran d\'une autre forme, la vue de départ est un peu plus zoomée.'}
				</Hint>
			</QuestionCard>

			<QuestionCard
				question="zoom"
				number={3}
				title="Jusqu'où peut-on zoomer ?"
				summary={`Jusqu'à ${formatFactor(framing.maxZoomFactor)}`}
				color={tokens.color.warning}
			>
				<Hint>Combien de fois plus près que la vue de départ :</Hint>

				<Segmented
					block
					size="small"
					value={framingStore.freeZoom ? 'free' : framing.maxZoomFactor}
					onChange={value => value === 'free' ? framingStore.setFreeZoom(true) : setMaxZoomFactor(Number(value))}
					options={[
						...FRAMING_MAX_ZOOM_FACTORS.map(factor => ({ value: factor, label: `×${factor}` })),
						{ value: 'free', label: 'Libre' },
					]}
				/>

				{framingStore.freeZoom && (
					<SliderInputNumber
						min={1}
						max={FREE_ZOOM_MAX_FACTOR}
						step={0.1}
						suffix="×"
						value={framing.maxZoomFactor}
						onChange={value => setFreeMaxZoomFactor(Number(value) || 1, true)}
						onAfterChange={value => setFreeMaxZoomFactor(Number(value) || 1)}
					/>
				)}

				<Hint>
					{`Au plus près, on voit ${formatFactor(framing.maxZoomFactor).slice(1)} fois plus de détail que sur la vue de départ${isPlaced && detailLevel ? ` (≈ niveau ${detailLevel})` : ''}.`}
				</Hint>
			</QuestionCard>
		</View>
	);
});

export default FramingSection;

function extendedLimit(framing: MapFraming, outsidePoints: [number, number][]): MapFraming['limit'] {
	if (framing.limit.mode !== 'frame') return framing.limit;

	const { frame } = framing.limit;
	const around = rectAroundPoints([...rectCorners(frame, framing.rotationDeg), ...outsidePoints], framing.rotationDeg)!;
	const pad = Math.max(around.width, around.height) * 0.05;

	return {
		mode: 'frame',
		frame: { center: around.center, width: around.width + 2 * pad, height: around.height + 2 * pad },
	};
}

const QuestionCard = observer((props: {
	question: FramingQuestion;
	number: number;
	title: string;
	summary: string;
	color: string;
	warning?: string;
	children: React.ReactNode;
}) => {
	const tokens = useDesignTokens();
	const open = framingStore.question === props.question;

	return (
		<View
			br={10}
			bg={tokens.surface.card}
			style={{
				border: `1px solid ${open ? tokens.color.primary : tokens.border.default}`,
				overflow: 'hidden',
			}}
		>
			<View
				row
				centerV
				gap="sm"
				p="sm"
				pointer
				hover={{ bg: tokens.surface.subtle }}
				onClick={() => framingStore.toggle(props.question)}
			>
				<View
					w={24}
					h={24}
					br={12}
					center
					bold
					size={12.5}
					color={tokens.surface.card}
					bg={props.color}
					style={{ flexShrink: 0 }}
				>
					{props.number}
				</View>

				<View flex gap={2}>
					<View bold size={13.5}>{props.title}</View>

					{!open && <View size={12.5} color={tokens.text.muted}>{props.summary}</View>}

					{!open && !!props.warning && (
						<div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: tokens.color.warning }}>
							<WarningOutlined style={{ flexShrink: 0 }} />
							<span>{props.warning}</span>
						</div>
					)}
				</View>

				<DownOutlined
					style={{ fontSize: 10, color: tokens.text.muted, transition: 'transform .2s ease' }}
					rotate={open ? 180 : 0}
				/>
			</View>

			{open && (
				<View gap="sm" px="sm" pb="sm">
					{props.children}
				</View>
			)}
		</View>
	);
});

function Choice(props: {
	label: string;
	hint: string;
	selected: boolean;
	onSelect: () => void;
	children?: React.ReactNode;
}) {
	const tokens = useDesignTokens();

	return (
		<View
			gap="xs"
			p="xs"
			br={8}
			bg={props.selected ? `color-mix(in srgb, ${tokens.color.primary} 8%, transparent)` : tokens.surface.card}
			style={{ border: `1px solid ${props.selected ? tokens.color.primary : tokens.border.default}` }}
		>
			<View row gap="xs" pointer onClick={props.onSelect}>
				<View flex gap={2}>
					<View bold size={13}>{props.label}</View>
					<View size={12} color={tokens.text.muted} lineHeight={1.35}>{props.hint}</View>
				</View>

				<CheckOutlined
					style={{ flexShrink: 0, fontSize: 12, opacity: props.selected ? 1 : 0, color: tokens.color.primary }}
				/>
			</View>

			{props.selected && props.children}
		</View>
	);
}

function normalizeDegrees(value: number) {
	return ((((value + 180) % 360) + 360) % 360) - 180;
}

function Compass({ rotationDeg }: { rotationDeg: number; }) {
	const tokens = useDesignTokens();

	return (
		<svg width={16} height={16} viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0, marginRight: 2, transform: `rotate(${rotationDeg}deg)`, transition: 'transform .2s ease' }}>
			<path d="M12 2 L16.5 12 L7.5 12 Z" fill={tokens.color.primary} />
			<path d="M12 22 L7.5 12 L16.5 12 Z" fill={tokens.text.disabled} />
		</svg>
	);
}

function SubField({ label, children }: { label: string; children: React.ReactNode; }) {
	return (
		<View gap="xs" pt="xs">
			<View bold size={12.5}>{label}</View>
			{children}
		</View>
	);
}

function Hint({ children }: { children: React.ReactNode; }) {
	const tokens = useDesignTokens();

	return <View size={12} color={tokens.text.muted} lineHeight={1.4}>{children}</View>;
}
